/**
 * @file WmsDocumentPageHeader.tsx
 * @description Шапка складского документа на Commora (phase19.31, зона
 * «Склад», минимум по SC50; uiux-design §5 «Карточка документа»).
 *
 * В верхнюю панель уходят «назад», номер документа, статус-пилюля,
 * пояснение («склад · поставщик»), «⋯» и ОДНО главное действие
 * (`CabinetPageHeader`); на телефоне и планшете главное действие — в общей
 * нижней полосе каркаса, причина блокировки — подписью над ним. Своей
 * липкой полосы у документа больше нет (одна нижняя полоса на экран).
 *
 * В содержимом остаются плитки «кто / когда / итоги» (`DocumentMetaGrid`) —
 * обычным шрифтом, и на широком экране — строка причины, почему главное
 * действие недоступно («Добавьте хотя бы одну позицию»).
 *
 * Старая шапка `WmsDocumentHeader` осталась у карточки заказа (зона
 * «Заказы» переводится отдельно); складские документы её не используют.
 */

import type { ReactNode } from "react";

import { CabinetPageHeader } from "../ui/CabinetPageHeader";
import { DocumentMetaGrid, type DocumentMetaItem } from "../ui/DocumentMetaGrid";
import type { PageAction } from "../ui/page-actions";

interface Props {
  /** Номер документа — название страницы: «RCP-2026-000020». */
  number: string;
  /** Статус-пилюля по реестру (`StatusPill`). */
  status: ReactNode;
  /** «Основной · ООО Травы · накладная 15». */
  subtitle: string;
  /** «Назад» — к реестру документов. */
  back: { to: string; label: string };
  /** Плитки «Создал / Создано / Проведено / Итого». */
  meta?: DocumentMetaItem[];
  /** Главное действие (одна кнопка). */
  action?: ReactNode;
  /** Пункты «⋯»: печать, звук скана, отмена (опасное — последним). */
  menu?: PageAction[];
  menuLabel: string;
  /** Почему главное действие недоступно или пометка «3 без срока». */
  hint?: string | null;
  /** Диалоги подтверждения пунктов меню. */
  dialogs?: ReactNode;
}

export function WmsDocumentPageHeader({ number, status, subtitle, back, meta, action, menu = [], menuLabel, hint, dialogs }: Props) {
  return (
    <div className="flex flex-col gap-3">
      <CabinetPageHeader
        title={number}
        status={status}
        subtitle={subtitle}
        back={back}
        action={action}
        menu={menu}
        menuLabel={menuLabel}
        mobileNote={hint ?? undefined}
      />
      {hint ? (
        <p className="type-caption hidden text-[var(--status-attention-fg)] lg:block" role="note">
          {hint}
        </p>
      ) : null}
      {meta && meta.length > 0 ? <DocumentMetaGrid items={meta} /> : null}
      {dialogs}
    </div>
  );
}
